import * as sentry from '@sentry/react';
import {call, put, select, takeLatest} from 'redux-saga/effects';

import {config} from 'config/env';
import {getAllStoreAreasV2, getStoreArea} from 'services/location/location-service';
import {getMakroMembershipCardsv2, getStoreAreas, getUserDetails} from 'services/user/user-service';
import {
  fetchMakroMembershipCardAction,
  setMembershipCardsAction,
  setDefaultMembershipCardAction,
  getStoreAreasAction,
  setStoreAreasAction,
  setIsAppLoadingAction,
  setCurrentStoreWithLocationLocalesAction,
  setCurrentStoreAction
} from 'store/actions/app-action';
import {fetchUserDetailsAction, setUserDetailsAction} from 'store/actions/auth-action';
import {CurrentStoreType, getStoreAreaFilter} from 'store/types/app';
import {SubDistrict} from 'types/location';
import {MembershipCard} from 'types/membership';

function* fetchMakroMembershipCardSaga(action: {payload: string}): Generator {
  try {
    yield put(setIsAppLoadingAction(true));
    const accessToken = action.payload;
    const response = (yield call(() => getMakroMembershipCardsv2(accessToken as string))) as MembershipCard[];
    const defaultMembershipCard = response?.find((card) => card.isDefault);
    yield put(setMembershipCardsAction(response));
    yield put(setDefaultMembershipCardAction(defaultMembershipCard));
  } catch (error) {
    sentry.captureException(error);
  } finally {
    yield put(setIsAppLoadingAction(false));
  }
}

function* fetchStoreAreasSaga(): Generator {
  try {
    const response: any = yield call(() => (config.IS_LOCATION_SVC_ENABLED ? getAllStoreAreasV2() : getStoreAreas()));
    const storeAreas = config.IS_LOCATION_SVC_ENABLED ? response : response?.data?.getStoreAreas;
    if (storeAreas) {
      yield put(setStoreAreasAction(storeAreas));
    }
  } catch (e: any) {
    sentry.captureException(e);
  }
}

function* setCurrentStoreWithLocationLocales(action: {payload: CurrentStoreType}): Generator {
  try {
    const payload: getStoreAreaFilter = {
      subDistrictName: action.payload.subdistrict,
      zipCode: action.payload.zipcode,
      exactMatch: true
    };
    const response: SubDistrict = (yield call(() => getStoreArea(payload))) as SubDistrict;
    if (response && action.payload) {
      yield put(
        setCurrentStoreAction({...action.payload, subdistrictEn: response.nameEn, subdistrictTh: response.nameTh})
      );
    }
  } catch (e: any) {
    sentry.captureException(e);
  }
}

function* fetchUserDetailsSaga(): Generator {
  const accessToken = yield select((state) => state.auth?.idToken);
  try {
    const response: any = yield call(() => getUserDetails(accessToken as string));
    const userDetails = response?.data?.getUserDetails;

    if (userDetails) {
      yield put(setUserDetailsAction(userDetails));
    }
  } catch (e: any) {
    const extra = {idToken: accessToken};
    sentry.captureException(e, {extra});
  }
}

export default function* () {
  yield takeLatest(fetchMakroMembershipCardAction, fetchMakroMembershipCardSaga);
  yield takeLatest(getStoreAreasAction, fetchStoreAreasSaga);
  yield takeLatest(fetchUserDetailsAction, fetchUserDetailsSaga);
  yield takeLatest(setCurrentStoreWithLocationLocalesAction, setCurrentStoreWithLocationLocales);
}
